/**
 * Đọc mẫu bằng giọng máy của trình duyệt (Web Speech API `speechSynthesis`) — miễn phí,
 * chạy hoàn toàn ở frontend, không gọi server (2026-10-03).
 *
 * Chất lượng/giọng phụ thuộc hệ điều hành: macOS/iOS/Android/Chrome thường có đủ
 * Nhật/Anh/Trung/Hàn; một số máy Windows thiếu giọng Nhật/Hàn → `hasVoiceFor()` = false.
 */

const LANG_BCP47: Record<string, string> = {
  ja: "ja-JP",
  en: "en-US",
  zh: "zh-CN",
  ko: "ko-KR",
};

export function speechSupported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/** Danh sách giọng nạp bất đồng bộ — chờ tối đa ~1.5s lần đầu. */
export function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  if (!speechSupported()) return Promise.resolve([]);
  const synth = window.speechSynthesis;
  const now = synth.getVoices();
  if (now.length) return Promise.resolve(now);
  return new Promise((resolve) => {
    const done = () => resolve(synth.getVoices());
    synth.addEventListener("voiceschanged", done, { once: true });
    setTimeout(done, 1500);
  });
}

function pickVoice(voices: SpeechSynthesisVoice[], language: string) {
  const tag = (LANG_BCP47[language] ?? language).toLowerCase();
  const prefix = tag.split("-")[0];
  const matches = voices.filter((v) => v.lang.toLowerCase().replace("_", "-").startsWith(prefix));
  return (
    matches.find((v) => v.lang.toLowerCase().replace("_", "-") === tag && v.localService) ??
    matches.find((v) => v.lang.toLowerCase().replace("_", "-") === tag) ??
    matches[0] ??
    null
  );
}

export async function hasVoiceFor(language: string): Promise<boolean> {
  return pickVoice(await loadVoices(), language) !== null;
}

/** Đọc `text` bằng ngôn ngữ `language` (ja|en|zh|ko). Resolve khi đọc xong. */
export async function speak(text: string, language: string, rate = 0.85): Promise<void> {
  if (!speechSupported() || !text.trim()) return;
  const synth = window.speechSynthesis;
  const voice = pickVoice(await loadVoices(), language);
  synth.cancel(); // dừng câu đang đọc (bấm liên tiếp)
  await new Promise<void>((resolve) => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = LANG_BCP47[language] ?? language;
    if (voice) u.voice = voice;
    u.rate = rate;
    u.onend = () => resolve();
    u.onerror = () => resolve();
    synth.speak(u);
  });
}

export function stopSpeaking() {
  if (speechSupported()) window.speechSynthesis.cancel();
}
